/**
 * Widget utilities for resolving template variables in widget payloads
 * and auto-generating widgets for specific trigger and action types
 */

import type { SheetData } from "@/types/aiChat";
import { resolvePropsInPayload } from "./resolveProps";

export { resolvePropsInPayload };

export const ZERO_ADDRESS = "0x0000000000000000000000000000000000000000";

/** Internal widget type for auto-generated trigger widgets */
interface Widget {
  type: string;
  label: string;
  action: string;
  payload: Record<string, unknown>;
}

/**
 * Auto-generates widgets for specific trigger types based on their payload.
 * Returns an array of widgets that make sense for the given trigger type.
 * Resolves {{$props.X}} references in the payload using propValues.
 */
export function getAutoWidgetsForTrigger(
  triggerType: string | undefined,
  payload: Record<string, unknown> | undefined,
  propValues?: Record<string, unknown>,
  isPublished?: boolean,
): Widget[] {
  if (!triggerType || !payload) return [];

  // Resolve {{$props.X}} references so widget values use actual addresses.
  const resolved = propValues ? resolvePropsInPayload(payload, propValues) : payload;

  switch (triggerType) {
    case "erc20-receive": {
      // Only show when published — trigger must be actively listening for transfers
      if (!isPublished) break;
      const toAddress = typeof resolved.toAddress === "string" ? resolved.toAddress : undefined;
      const tokenAddress = typeof resolved.tokenAddress === "string" ? resolved.tokenAddress : undefined;
      const chainId = typeof resolved.chainId === "number" ? resolved.chainId : undefined;

      if (toAddress && chainId) {
        return [
          {
            type: "anyspend-transfer",
            label: "Send tokens to trigger",
            action: "anySpendDeposit",
            payload: {
              recipientAddress: toAddress,
              ...(tokenAddress ? { destinationTokenAddress: tokenAddress } : {}),
              destinationTokenChainId: chainId,
              actionLabel: "Send tokens to trigger this workflow",
            },
          },
        ];
      }
      break;
    }

    case "erc20-send": {
      // Only show when published — trigger must be actively listening for transfers
      if (!isPublished) break;
      const fromAddress = typeof resolved.fromAddress === "string" ? resolved.fromAddress : undefined;
      const tokenAddress = typeof resolved.tokenAddress === "string" ? resolved.tokenAddress : undefined;
      const chainId = typeof resolved.chainId === "number" ? resolved.chainId : undefined;

      if (fromAddress && chainId) {
        return [
          {
            type: "anyspend-transfer",
            label: "Fund wallet to trigger",
            action: "anySpendDeposit",
            payload: {
              recipientAddress: fromAddress,
              ...(tokenAddress ? { destinationTokenAddress: tokenAddress } : {}),
              destinationTokenChainId: chainId,
              actionLabel: "Fund the wallet that will send tokens",
            },
          },
        ];
      }
      break;
    }

    case "anyspend": {
      // Only show when published — trigger must be actively listening for payments
      if (!isPublished) break;
      const recipientAddress = typeof resolved.recipientAddress === "string" ? resolved.recipientAddress : undefined;
      const chainId = typeof resolved.chainId === "number" ? resolved.chainId : undefined;
      const tokenAddress = typeof resolved.tokenAddress === "string" ? resolved.tokenAddress : undefined;
      const amount = typeof resolved.amount === "string" ? resolved.amount : undefined;

      if (recipientAddress && chainId) {
        return [
          {
            type: "anyspend-transfer",
            label: "Make a payment",
            action: "anySpendWorkflowTrigger",
            payload: {
              recipientAddress,
              tokenAddress: tokenAddress || ZERO_ADDRESS,
              chainId,
              amount: amount || "",
              actionLabel: "Make a payment to trigger this workflow",
            },
          },
        ];
      }
      break;
    }
  }

  return [];
}

/**
 * Resolves template variables in a widget payload.
 * Templates use the format {{root.payload.fieldName}} to reference trigger node payload values.
 *
 * @example
 * // Widget payload with templates
 * const widgetPayload = {
 *   recipientAddress: "{{root.payload.contractAddress}}",
 *   destinationTokenChainId: "{{root.payload.chainId}}"
 * };
 *
 * // Trigger node payload
 * const nodePayload = {
 *   contractAddress: "0x833589fCD6eDb6E08f4c7C32D4f71b54bdA02913",
 *   chainId: 8453
 * };
 *
 * // Resolved payload
 * const resolved = resolveWidgetPayload(widgetPayload, nodePayload);
 * // { recipientAddress: "0x833589fCD6eDb6E08f4c7C32D4f71b54bdA02913", destinationTokenChainId: 8453 }
 */
export function resolveWidgetPayload(
  widgetPayload: Record<string, unknown>,
  nodePayload: Record<string, unknown>,
): Record<string, unknown> {
  const resolved: Record<string, unknown> = {};

  for (const [key, value] of Object.entries(widgetPayload)) {
    resolved[key] = resolveValue(value, nodePayload);
  }

  return resolved;
}

/**
 * Resolves a single value, handling strings with templates, arrays, and nested objects.
 */
function resolveValue(value: unknown, nodePayload: Record<string, unknown>): unknown {
  if (typeof value === "string") {
    return resolveTemplateString(value, nodePayload);
  }

  if (Array.isArray(value)) {
    return value.map(item => resolveValue(item, nodePayload));
  }

  if (value !== null && typeof value === "object") {
    return resolveWidgetPayload(value as Record<string, unknown>, nodePayload);
  }

  return value;
}

/**
 * Resolves template variables in a string.
 * If the entire string is a single template (e.g., "{{root.payload.chainId}}"),
 * returns the original type (number, boolean, etc.) instead of a string.
 */
function resolveTemplateString(template: string, nodePayload: Record<string, unknown>): unknown {
  // Check if the entire string is a single template variable
  const singleTemplateMatch = template.match(/^\{\{root\.payload\.([^}]+)\}\}$/);
  if (singleTemplateMatch) {
    const fieldPath = singleTemplateMatch[1];
    return getNestedValue(nodePayload, fieldPath);
  }

  // Replace multiple template variables in the string
  return template.replace(/\{\{root\.payload\.([^}]+)\}\}/g, (_, fieldPath: string) => {
    const value = getNestedValue(nodePayload, fieldPath);
    return value !== undefined && value !== null ? String(value) : "";
  });
}

/**
 * Gets a nested value from an object using dot notation.
 * @example getNestedValue({ a: { b: 1 } }, "a.b") // returns 1
 */
function getNestedValue(obj: Record<string, unknown>, path: string): unknown {
  const parts = path.split(".");
  let current: unknown = obj;

  for (const part of parts) {
    if (current === null || current === undefined) {
      return undefined;
    }
    if (typeof current !== "object") {
      return undefined;
    }
    current = (current as Record<string, unknown>)[part];
  }

  return current;
}

// ============================================================================
// Action Widget Types and Registry
// ============================================================================

/** Widget types for displaying action results */
export type ActionWidgetType = "data-table" | "text-display" | "json-view" | "google-sheet";

/** Configuration for an action widget */
export interface ActionWidgetConfig {
  /** The type of widget to render */
  widgetType: ActionWidgetType;
  /** Path to the data within the result object (e.g., "filtered", "data", "formatted") */
  resultPath: string;
  /** Human-readable label for the widget */
  label: string;
  /** Optional description */
  description?: string;
}

/** An action widget instance with resolved data */
export interface ActionWidget {
  /** Unique identifier for this widget instance */
  id: string;
  /** The widget type */
  widgetType: ActionWidgetType;
  /** Human-readable label */
  label: string;
  /** The action type that produced this widget */
  actionType: string;
  /** The node ID that produced this widget */
  nodeId: string;
  /** Path to data in the result */
  resultPath: string;
  /** The actual data (null if not yet executed) */
  data: unknown;
  /** Whether data is available */
  hasData: boolean;
}

/**
 * Registry of action types that produce displayable data widgets.
 * Actions not in this registry will use automatic detection based on result schema.
 */
export const ACTION_WIDGET_REGISTRY: Record<string, ActionWidgetConfig> = {
  // -------------------------------------------------------------------------
  // Logic Actions
  // -------------------------------------------------------------------------
  filter: {
    widgetType: "data-table",
    resultPath: "filtered",
    label: "Filtered Results",
    description: "Array of items that matched the filter conditions",
  },
  format: {
    widgetType: "text-display",
    resultPath: "formatted",
    label: "Formatted Output",
    description: "Formatted string output",
  },

  // -------------------------------------------------------------------------
  // Coinglass - Futures Data
  // -------------------------------------------------------------------------
  "coinglass-get-liquidation-coin-list": {
    widgetType: "data-table",
    resultPath: "coins",
    label: "Liquidation Data",
    description: "Liquidation amounts for coins on futures exchanges",
  },
  "coinglass-get-coins-markets": {
    widgetType: "data-table",
    resultPath: "coins",
    label: "Coins Markets",
    description: "Market data for cryptocurrency coins",
  },
  "coinglass-get-pairs-markets": {
    widgetType: "data-table",
    resultPath: "coins",
    label: "Pairs Markets",
    description: "Market data for trading pairs",
  },
  "coinglass-get-funding-rate-list": {
    widgetType: "data-table",
    resultPath: "data",
    label: "Funding Rates",
    description: "Current funding rates across exchanges",
  },
  "coinglass-get-oi-coin-list": {
    widgetType: "data-table",
    resultPath: "data",
    label: "Open Interest",
    description: "Open interest data by coin",
  },
  "coinglass-get-oi-exchange-list": {
    widgetType: "data-table",
    resultPath: "data",
    label: "OI by Exchange",
    description: "Open interest data by exchange",
  },

  // -------------------------------------------------------------------------
  // Coinglass - Spot Data
  // -------------------------------------------------------------------------
  "coinglass-spot-get-coins-markets": {
    widgetType: "data-table",
    resultPath: "coins",
    label: "Spot Markets",
    description: "Spot market data for coins",
  },
  "coinglass-spot-get-pairs-markets": {
    widgetType: "data-table",
    resultPath: "coins",
    label: "Spot Pairs",
    description: "Spot market data for trading pairs",
  },

  // -------------------------------------------------------------------------
  // Coinglass - On-chain Data
  // -------------------------------------------------------------------------
  "coinglass-onchain-get-exchange-balance-list": {
    widgetType: "data-table",
    resultPath: "data",
    label: "Exchange Balances",
    description: "Token balances on exchanges",
  },
  "coinglass-onchain-get-exchange-transfers": {
    widgetType: "data-table",
    resultPath: "data",
    label: "Exchange Transfers",
    description: "Recent exchange transfer activity",
  },
  "coinglass-onchain-get-token-unlocks": {
    widgetType: "data-table",
    resultPath: "data",
    label: "Token Unlocks",
    description: "Upcoming token unlock events",
  },
  "coinglass-onchain-get-grayscale-holdings": {
    widgetType: "data-table",
    resultPath: "data",
    label: "Grayscale Holdings",
    description: "Grayscale trust holdings",
  },

  // -------------------------------------------------------------------------
  // Coinglass - Indicators
  // -------------------------------------------------------------------------
  "coinglass-indicator-get-rsi-list": {
    widgetType: "data-table",
    resultPath: "data",
    label: "RSI Indicators",
    description: "RSI values across coins",
  },

  // -------------------------------------------------------------------------
  // Coinglass - Macro Data
  // -------------------------------------------------------------------------
  "coinglass-macro-get-fear-greed": {
    widgetType: "data-table",
    resultPath: "data",
    label: "Fear & Greed Index",
    description: "Market sentiment indicator",
  },

  // -------------------------------------------------------------------------
  // X402 API Endpoints
  // -------------------------------------------------------------------------
  "call-x402-endpoint": {
    widgetType: "json-view",
    resultPath: "resource",
    label: "API Response",
    description: "Data fetched from x402 endpoint",
  },

  // -------------------------------------------------------------------------
  // Google Sheets
  // -------------------------------------------------------------------------
  "google-sheets-get-values": {
    widgetType: "google-sheet",
    resultPath: "data.values",
    label: "Sheet Data",
    description: "Values from Google Sheets",
  },
};

/**
 * Checks if an action type has a registered widget configuration.
 */
export function hasRegisteredWidget(actionType: string): boolean {
  return actionType in ACTION_WIDGET_REGISTRY;
}

/**
 * Gets the widget configuration for an action type.
 * Returns undefined if not registered.
 */
export function getWidgetConfig(actionType: string): ActionWidgetConfig | undefined {
  return ACTION_WIDGET_REGISTRY[actionType];
}

/**
 * Auto-detects if a result schema indicates the action produces displayable data.
 * Used as a fallback for actions not in the registry.
 *
 * @param resultSchema - The JSON Schema for the action's result
 * @returns Widget config if detectable, undefined otherwise
 */
export function detectWidgetFromSchema(
  resultSchema: Record<string, unknown> | undefined,
): ActionWidgetConfig | undefined {
  if (!resultSchema) return undefined;

  const properties = resultSchema.properties as Record<string, unknown> | undefined;
  if (!properties) return undefined;

  // Look for common array result patterns
  for (const [key, schema] of Object.entries(properties)) {
    const propSchema = schema as Record<string, unknown>;

    // Check if this property is an array of objects
    if (propSchema.type === "array") {
      const items = propSchema.items as Record<string, unknown> | undefined;
      if (items?.type === "object") {
        return {
          widgetType: "data-table",
          resultPath: key,
          label: formatLabel(key),
          description: (propSchema.description as string) || undefined,
        };
      }
    }
  }

  return undefined;
}

/**
 * Formats a camelCase or snake_case key into a human-readable label.
 */
function formatLabel(key: string): string {
  return key
    .replace(/([a-z\d])([A-Z])/g, "$1 $2")
    .replace(/([A-Z]+)([A-Z][a-z]{2,})/g, "$1 $2")
    .replace(/[_-]/g, " ")
    .replace(/^\s/, "")
    .split(" ")
    .map(word => word.charAt(0).toUpperCase() + word.slice(1))
    .join(" ");
}

/**
 * Extracts data from an execution result using a dot-notation path.
 */
export function extractResultData(result: Record<string, unknown> | undefined, resultPath: string): unknown {
  if (!result) return undefined;
  return getNestedValue(result, resultPath);
}

/**
 * Auto-generates widgets for action nodes based on their type and execution results.
 * Returns an array of widgets that can display the action's output data.
 *
 * @param actionType - The action type (e.g., "filter", "coinglass-get-liquidation-coin-list")
 * @param nodeId - The node ID in the workflow
 * @param resultSchema - Optional JSON Schema for the action's result
 * @param executionResult - Optional execution result data
 * @returns Array of action widgets
 */
export function getAutoWidgetsForAction(
  actionType: string | undefined,
  nodeId: string,
  resultSchema?: Record<string, unknown>,
  executionResult?: Record<string, unknown>,
): ActionWidget[] {
  if (!actionType) return [];

  // First, check the registry for known actions
  let config: ActionWidgetConfig | undefined = ACTION_WIDGET_REGISTRY[actionType];

  // If not in registry, try to detect from schema
  if (!config && resultSchema) {
    config = detectWidgetFromSchema(resultSchema);
  }

  // If still no config, return empty
  if (!config) return [];

  // Extract the data if we have execution results
  const data = executionResult ? extractResultData(executionResult, config.resultPath) : null;
  const hasData = data !== null && data !== undefined;

  return [
    {
      id: `${nodeId}-${config.resultPath}`,
      widgetType: config.widgetType,
      label: config.label,
      actionType,
      nodeId,
      resultPath: config.resultPath,
      data,
      hasData,
    },
  ];
}

/**
 * Gets all action widgets for an entire workflow based on execution state.
 *
 * @param nodes - Map of node IDs to their definitions
 * @param executionState - Map of node IDs to their execution state
 * @param actionDefinitions - Map of action types to their definitions (for result schemas)
 * @returns Array of all action widgets in the workflow
 */
export function getAllWorkflowWidgets(
  nodes: Record<string, { type: string; payload?: Record<string, unknown> }>,
  executionState?: Record<string, { result?: Record<string, unknown> }>,
  actionDefinitions?: Record<string, { resultSchema?: Record<string, unknown> }>,
): ActionWidget[] {
  const widgets: ActionWidget[] = [];

  for (const [nodeId, node] of Object.entries(nodes)) {
    const actionType = node.type;
    const resultSchema = actionDefinitions?.[actionType]?.resultSchema;
    const executionResult = executionState?.[nodeId]?.result;

    const nodeWidgets = getAutoWidgetsForAction(actionType, nodeId, resultSchema, executionResult);
    widgets.push(...nodeWidgets);
  }

  return widgets;
}

/**
 * Converts raw 2D array data (where first row = headers) to a SheetData object.
 * Used by action widgets where Google Sheets returns unknown[][] via resultPath.
 * Returns null if the data is not a valid array-of-arrays.
 */
export function rawRowsToSheetData(raw: unknown[]): SheetData | null {
  if (raw.length > 0 && !Array.isArray(raw[0])) {
    return null;
  }
  const rows = raw as unknown[][];
  return {
    headers: rows.length > 0 ? rows[0].map(String) : [],
    rows: rows.slice(1).map(r => (r as unknown[]).map(String)),
    total_rows: Math.max(0, rows.length - 1),
  };
}
